<template>
  <MarketCard
    :class="[classes.listCardWrapper, 'list']"
    style="position: relative;"
  >
    <CardHeader :eventId="eventId" />

    <div :class="classes.eventMarketWrapper">
      <EventName
        :class="[classes.eventName, 'event-name']"
        :marketId="marketId"
        :eventId="eventId"
        :useTeamNames="true"
      />
      <MarketName
        :marketId="marketId"
        :fallbackName="market.marketName"
        :class="['market-name', classes.marketName]"
      />
    </div>

    <div :class="[classes.listWrapper, layoutClass]">
      <ListOption
        v-for="(option, i) in options"
        :key="option.id"
        :stagger="i"
        :eventId="eventId"
        :option="option"
        :marketId="market.marketId"
      />
    </div>

    <div :class="classes.fade" />
  </MarketCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, ComputedRef, defineComponent } from "vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import ListOption from "../ListOption.vue";
import EventName from "../../../EventNameBadges.vue";
import MarketName from "../../../MarketName.vue";
import CardHeader from "../../../CardHeader.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import {
  composeMarket,
  composeListMarketOptions
} from "@fresh8/ad-data-composition/src/betData";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { Market, Option } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  listCardWrapper: {
    flex: "1 1 1px",
    flexDirection: "column",
    flexWrap: "wrap"
  },
  eventMarketWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingBottom: "8px",
    boxSizing: "border-box"
  },
  marketName: {
    margin: "0px 0",
    "$eventMarketWrapper &": {
      fontSize: "11px"
    }
  },
  eventName: {
    margin: "8px 0",
    alignSelf: "center",

    "& .competitors .banner": {
      maxWidth: "180px",
      "&.right": {
        marginLeft: props => (props.isCarousel ? "0" : "15px"),
        paddingRight: "8px"
      },
      "&.left": {
        marginRight: props => (props.isCarousel ? "0" : "15px"),
        paddingLeft: "8px"
      }
    },
    "& .vs": {
      placeSelf: "center",
      width: "25px"
    }
  },
  listWrapper: {
    width: "100%",
    overflowX: "auto",
    boxSizing: "border-box",
    position: "relative",
    display: "grid",
    padding: "0 10px",
    gridAutoFlow: "row",
    gap: "4px",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gridTemplateRows: "repeat(auto-fit, max(28px))",
    gridAutoRows: "max(28px)",
    flex: "1",
    [`&.billboard`]: {
      maxHeight: "130px"
    },
    [`&.mpu`]: {
      maxHeight: "118px"
    },
    [`&.large-responsive`]: {
      maxHeight: "132px"
    }
  },

  fade: {
    pointerEvents: "none",
    position: "absolute",
    height: "22px",
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: "0 0 4px 4px",
    background: props =>
      props.backgroundImage
        ? "none"
        : `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${props.cardBackgroundColor ||
            props.backgroundColor} 79.17%)`
  },
  spacer: {
    height: "3px"
  }
});

export default defineComponent({
  name: "MPU",
  components: {
    ListOption,
    CardHeader,
    EventName,
    MarketCard,
    MarketName
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brandContext = injectBrand();
    const carouselContext = injectCarouselContext();
    const templateSizeContext = injectTemplateSize();

    const backgroundImage = computed(() => {
      return !!Object.keys(brandContext?.backgroundImageSets?.value || {})
        .length;
    });

    const relativeStyles = computed(() => ({
      ...(brandContext?.adTemplateBranding.value || {}),
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);
    const market = composeMarket(props.marketId) as ComputedRef<Market>;
    const marketOptions = composeListMarketOptions(
      props.marketId
    ) as ComputedRef<Option[]>;

    return {
      layoutClass: computed(() => templateSizeContext?.size.value.name),
      market,
      marketBranding: brandContext?.marketBranding,
      classes: classesRef,
      options: marketOptions
    };
  }
});
</script>
